// Selection frame and its handles. A single object's frame is the object's own
// (possibly rotated) box; a multi-selection's frame is the axis-aligned union.
import type { BoardObject } from '../../shared/types.ts';
import type { Store } from '../model/store.ts';
import { aabb, toLocal, union, type Box, type Rect } from '../util/geometry.ts';

export type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';
export type Handle = ResizeHandle | 'rotate';

export const HANDLE_SIZE = 8;     // screen px
const HANDLE_HIT = 8;             // screen px, half-width of a handle's hit square
const EDGE_HIT = 4;               // screen px either side of an edge
export const ROTATE_OFFSET = 24;  // screen px above the top edge
const MIN_SIZE = 4;               // world units

export function selectionFrame(store: Store, ids: ReadonlySet<string>, boundsOf: (o: BoardObject) => Rect = aabb): Box | null {
  if (ids.size === 1) {
    const o = store.get(ids.values().next().value!);
    if (o?.type === 'connector') return { ...boundsOf(o), rotation: 0 };
    return o ? { x: o.x, y: o.y, w: o.w, h: o.h, rotation: o.rotation } : null;
  }
  const rects: Rect[] = [];
  for (const id of ids) {
    const o = store.get(id);
    if (o) rects.push(boundsOf(o));
  }
  const r = union(rects);
  return r ? { ...r, rotation: 0 } : null;
}

/** Handle positions in frame-local coords. Edge handles hide when the frame is small on screen. */
export function handlePoints(frame: Box, zoom: number, rotate = true): [Handle, number, number][] {
  const { w, h } = frame;
  const pts: [Handle, number, number][] = [['nw', 0, 0], ['ne', w, 0], ['se', w, h], ['sw', 0, h]];
  if (w * zoom > 40) pts.push(['n', w / 2, 0], ['s', w / 2, h]);
  if (h * zoom > 40) pts.push(['e', w, h / 2], ['w', 0, h / 2]);
  if (rotate) pts.push(['rotate', w / 2, -ROTATE_OFFSET / zoom]);
  return pts;
}

export function handleAt(frame: Box, zoom: number, wx: number, wy: number, rotate = true): Handle | null {
  const [lx, ly] = toLocal(frame, wx, wy);
  const r = HANDLE_HIT / zoom;
  for (const [h, hx, hy] of handlePoints(frame, zoom, rotate)) {
    if (Math.abs(lx - hx) <= r && Math.abs(ly - hy) <= r) return h;
  }
  // Grabbing anywhere along an edge resizes too, unless the frame is tiny on screen.
  if (frame.w * zoom < 24 || frame.h * zoom < 24) return null;
  const e = EDGE_HIT / zoom;
  const inX = lx > 0 && lx < frame.w;
  const inY = ly > 0 && ly < frame.h;
  if (inX && Math.abs(ly) <= e) return 'n';
  if (inX && Math.abs(ly - frame.h) <= e) return 's';
  if (inY && Math.abs(lx) <= e) return 'w';
  if (inY && Math.abs(lx - frame.w) <= e) return 'e';
  return null;
}

const HANDLE_ANGLE: Record<ResizeHandle, number> = { e: 0, se: 45, s: 90, sw: 135, w: 180, nw: 225, n: 270, ne: 315 };
const RESIZE_CURSORS = ['ew-resize', 'nwse-resize', 'ns-resize', 'nesw-resize'];

export function handleCursor(handle: Handle, rotation: number): string {
  if (handle === 'rotate') return 'grab';
  const deg = HANDLE_ANGLE[handle] + (rotation * 180) / Math.PI;
  return RESIZE_CURSORS[Math.round((((deg % 180) + 180) % 180) / 45) % 4]!;
}

/**
 * New frame-local rect after dragging `handle` to local point (lx, ly). The
 * opposite edge stays anchored; edges can't cross (no flipping).
 */
export function resizeLocal(frame: Box, handle: ResizeHandle, lx: number, ly: number, keepAspect: boolean): Rect {
  const movesW = handle.includes('w'), movesE = handle.includes('e');
  const movesN = handle.includes('n'), movesS = handle.includes('s');
  let x0 = 0, y0 = 0, x1 = frame.w, y1 = frame.h;
  if (movesW) x0 = Math.min(lx, x1 - MIN_SIZE);
  if (movesE) x1 = Math.max(lx, x0 + MIN_SIZE);
  if (movesN) y0 = Math.min(ly, y1 - MIN_SIZE);
  if (movesS) y1 = Math.max(ly, y0 + MIN_SIZE);

  if (keepAspect && frame.w > 0 && frame.h > 0) {
    const ratio = frame.w / frame.h;
    let w = x1 - x0, h = y1 - y0;
    if (handle.length === 2) {
      if (w / h > ratio) h = w / ratio; else w = h * ratio;
      if (movesW) x0 = x1 - w; else x1 = x0 + w;
      if (movesN) y0 = y1 - h; else y1 = y0 + h;
    } else if (movesW || movesE) {
      h = w / ratio;
      y0 = frame.h / 2 - h / 2;
      y1 = y0 + h;
    } else {
      w = h * ratio;
      x0 = frame.w / 2 - w / 2;
      x1 = x0 + w;
    }
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}
